/**
 * Applicant active-liveness capture — real client-side challenge driving the
 * MetaMap-style oval stage. The challenge SEQUENCER + anti-spoof gate are the
 * pure, backend-agnostic ports of the reference Flutter `LivenessDetector` /
 * `SpoofingDetector` (see ./ml/challenge-machine, ./ml/anti-spoof).
 *
 * Live phase: front camera under an oval guide. A MediaPipe FaceMesh model
 * (lazy-loaded from `./ml`) runs per-frame detection. A single pure
 * `LivenessChallengeMachine` sequences a CONFIG-DRIVEN, shuffled active challenge
 * set (smile / blink / turn-left / turn-right, with nod / head-shake supported)
 * that only advances when a live human performs each action, with a
 * positioning + stability gate and a wait-for-neutral between challenges. A
 * `SpoofingDetector` runs alongside over the numeric motion history. When every
 * challenge is complete AND the anti-spoof gate reports live, the attempt
 * finalizes: a still frame is grabbed and the whole attempt is recorded as PAD
 * video.
 *
 * Review phase: circular selfie preview, quality callouts, Retake / Submit.
 *
 * Trust boundary: the client liveness OUTCOME (challenge results, anti-spoof
 * summary, numeric per-frame history) is ADVISORY only. It rides along as
 * `client_liveness` (see capture.ts) but the server ML sidecar remains the sole
 * scoring authority and re-computes its own verdict from the reported numeric
 * history — the browser is never trusted to grade its own selfie.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  blobToBase64,
  grabFrame,
  submitCapturedSelfie,
  toClientLivenessSignal,
} from './capture';
import { livenessErrorKey } from './livenessErrors';
import { classifyLivenessQuality } from './attemptReason';
import { CollectionFlowError, startLivenessSession, submitLivenessStep } from './client';
import { noteCamera } from './telemetry';
import { assessSelfieQuality, type QualityIssue } from './livenessQuality';
import { reportAttempt } from './reportAttempt';
import { FlashStage } from './FlashStage';
import type {
  ChallengeReportFrame,
  CollectionFlowSnapshot,
  FlashSegment,
  LivenessSession,
} from './types';
// Type-only imports (erased) — the runtime module is code-split via dynamic
// import('./ml') so MediaPipe + the model chunk load only when this step mounts.
import type {
  DistanceHint,
  LivenessOutcome,
  LivenessScorer,
} from './ml/liveness-scorer';
import type {
  AntiSpoofFrame,
  ChallengeType,
  LivenessChallengeMachine,
  MachineState,
  SpoofingDetector,
} from './ml';
import { StatusGlyph } from '../ui/StatusGlyph';

type MlModule = typeof import('./ml');
type Phase = 'starting' | 'detecting' | 'captured' | 'submitting' | 'denied';

const FRAME_INTERVAL_MS = 250;
// Cap the reported numeric history to the anti-spoof ring size (matches the
// server-side re-validation window; keeps the payload small + PDPA-minimal).
const MAX_REPORTED_HISTORY = 30;
// PAD-clip bitrate cap (~1 Mbps). The server re-validates liveness from the
// numeric per-frame history (challenge_report), NOT the video pixels, so the clip
// is only supplementary evidence. The browser default (~2.5 Mbps) recording the
// whole attempt blows past the upload body limit (24 MiB → base64) and 413s;
// ~1 Mbps keeps even a worst-case ~90 s attempt (4 challenges × 20 s timeouts)
// well under it (~11 MiB → ~15 MiB base64).
const PAD_VIDEO_BITRATE = 1_000_000;
const RECORDER_MIME_TYPES = [
  'video/webm;codecs=vp9',
  'video/webm;codecs=vp8',
  'video/webm',
  'video/mp4;codecs=h264',
  'video/mp4',
];

/** Pick a supported MediaRecorder, or null when recording is unavailable (e.g. jsdom). */
function makeRecorder(stream: MediaStream): MediaRecorder | null {
  if (typeof MediaRecorder === 'undefined') return null;
  const chosen = RECORDER_MIME_TYPES.find((type) => MediaRecorder.isTypeSupported(type));
  const options: MediaRecorderOptions = {
    ...(chosen ? { mimeType: chosen } : {}),
    videoBitsPerSecond: PAD_VIDEO_BITRATE,
  };
  try {
    return new MediaRecorder(stream, options);
  } catch {
    // Some engines reject the bitrate hint — retry with just the mime type.
    try {
      return chosen ? new MediaRecorder(stream, { mimeType: chosen }) : new MediaRecorder(stream);
    } catch {
      return null;
    }
  }
}

/** Stop the recorder and read the accumulated chunks as base64; null if nothing recorded. */
function stopRecorderToBase64(
  recorder: MediaRecorder | null,
  chunks: Blob[],
): Promise<string | null> {
  return new Promise((resolve) => {
    if (!recorder || recorder.state === 'inactive') {
      resolve(null);
      return;
    }
    recorder.onstop = () => {
      const type = (recorder.mimeType || 'video/webm').split(';')[0] || 'video/webm';
      const blob = new Blob(chunks, { type });
      if (blob.size === 0) {
        resolve(null);
        return;
      }
      blobToBase64(blob)
        .then(resolve)
        .catch(() => resolve(null));
    };
    try {
      recorder.stop();
    } catch {
      resolve(null);
    }
  });
}

interface CaptureLivenessProps {
  token: string;
  flowId: string;
  /** Called with the next snapshot once liveness is submitted and the flow moves on. */
  onAdvance: (snapshot: CollectionFlowSnapshot) => void;
  /**
   * Escape hatch offered when the camera cannot start — see the identical prop
   * on {@link CaptureDocument}. Present only on a desktop that opted out of the
   * phone handoff and can still return to the QR.
   */
  onCameraUnavailable?: () => void;
}

export function CaptureLiveness({
  token,
  flowId,
  onAdvance,
  onCameraUnavailable,
}: CaptureLivenessProps) {
  const { t } = useTranslation();
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [flash, setFlash] = useState<null | {
    challengeId: string;
    sequence: FlashSegment[];
    stream: MediaStream | null;
  }>(null);
  const flashClipRef = useRef<null | string>(null);
  const mlRef = useRef<MlModule | null>(null);
  const scorerRef = useRef<LivenessScorer | null>(null);
  const machineRef = useRef<LivenessChallengeMachine | null>(null);
  const antiSpoofRef = useRef<SpoofingDetector | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const capturedVideoRef = useRef<string | null>(null);
  const distanceHintRef = useRef<DistanceHint>('no_face');
  const finalizedRef = useRef(false);
  const calibratedRef = useRef(false);
  const firstFrameMsRef = useRef<number | null>(null);
  // Bounded numeric per-frame history — travels top-level as `challenge_report`
  // for the server to re-derive its own verdict from (never a client boolean).
  const historyRef = useRef<ChallengeReportFrame[]>([]);
  // The active challenge session. One challenge is in flight at a time and the
  // next is not knowable until this one is accepted, so there is nothing here
  // resembling a plan — only the outstanding step.
  const sessionRef = useRef<LivenessSession | null>(null);
  // Frames belonging to the CURRENT step only. Kept apart from `historyRef`,
  // which is the whole-run report and is FIFO-capped: submitting a slice of a
  // capped buffer would silently drop the beginning of a long step.
  const stepFramesRef = useRef<ChallengeReportFrame[]>([]);
  // Guards re-entry while a step submission is in flight. The frame loop fires
  // every FRAME_INTERVAL_MS and would otherwise submit the same challenge many
  // times over — which the server refuses as a replay, but which would also
  // burn the step before the applicant saw the next prompt.
  const stepBusyRef = useRef(false);

  const [cameraReady, setCameraReady] = useState(false);
  const [modelReady, setModelReady] = useState(false);
  const [phase, setPhase] = useState<Phase>('starting');
  // Machine-derived UI state: the current challenge + progress, or null while
  // positioning. `order` is the shuffled challenge sequence for the dot rail.
  const [machineState, setMachineState] = useState<MachineState | null>(null);
  const [challengeOrder, setChallengeOrder] = useState<ChallengeType[]>([]);
  const [antiSpoofReason, setAntiSpoofReason] = useState<string | null>(null);
  const [distanceHint, setDistanceHint] = useState<DistanceHint>('no_face');
  const [base64, setBase64] = useState<string | null>(null);
  const [outcome, setOutcome] = useState<LivenessOutcome | null>(null);
  const [issues, setIssues] = useState<QualityIssue[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [retakeNonce, setRetakeNonce] = useState(0);

  const stopCamera = useCallback(() => {
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
  }, []);

  // Mount: open the front camera + lazy-load the FaceMesh model in parallel.
  useEffect(() => {
    let cancelled = false;

    async function startCamera() {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'user', width: { ideal: 1280 }, height: { ideal: 720 } },
          audio: false,
        });
        if (cancelled) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }
        streamRef.current = stream;
        // Telemetry only; it must never be able to fail the camera start.
        noteCamera(stream.getVideoTracks?.()[0]);
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await Promise.resolve(videoRef.current.play()).catch(() => undefined);
        }
        setCameraReady(true);
      } catch {
        if (!cancelled) setPhase('denied');
      }
    }

    async function loadModel() {
      try {
        const ml = await import('./ml');
        if (cancelled) return;
        mlRef.current = ml;
        await ml.loadFaceMesh();
        if (cancelled) return;
        scorerRef.current = new ml.LivenessScorer();
        antiSpoofRef.current = new ml.SpoofingDetector(ml.DEFAULT_LIVENESS_CONFIG);

        // The challenge comes from the SERVER, drawn from a CSPRNG against a
        // pool this bundle cannot see. The machine is constructed with that one
        // challenge and gains each subsequent one only after the previous is
        // accepted — it no longer picks, or knows, a sequence.
        // Photosensitivity: a reduced-motion preference declines the flash
        // step up front; the server records the decline and plans none.
        const reducedMotion =
          typeof window.matchMedia === 'function' &&
          window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        const session = await startLivenessSession(token, reducedMotion ? { flash: false } : {});
        if (cancelled) return;
        sessionRef.current = session;

        const first = session.challenge?.action as ChallengeType | undefined;
        if (!first) {
          setError('liveness_session_unavailable');
          return;
        }
        machineRef.current = new ml.LivenessChallengeMachine([first]);
        setChallengeOrder([first]);
        setModelReady(true);
      } catch {
        if (!cancelled) setError('model_load_failed');
      }
    }

    void startCamera();
    void loadModel();

    return () => {
      cancelled = true;
      stopCamera();
    };
  }, [stopCamera]);

  // Detection loop: runs once camera + model are ready (and restarts on retake).
  // Owns the per-attempt lifecycle: fresh scorer + machine + anti-spoof,
  // MediaRecorder, frame interval.
  useEffect(() => {
    if (!cameraReady || !modelReady) return;
    const ml = mlRef.current;
    const scorer = scorerRef.current;
    const machine = machineRef.current;
    const antiSpoof = antiSpoofRef.current;
    const stream = streamRef.current;
    if (!ml || !scorer || !machine || !antiSpoof || !stream) return;

    // Reset attempt state.
    scorer.reset();
    machine.reset();
    antiSpoof.reset();
    calibratedRef.current = false;
    firstFrameMsRef.current = null;
    historyRef.current = [];
    stepFramesRef.current = [];
    stepBusyRef.current = false;
    setMachineState(machine.state());
    setAntiSpoofReason(null);
    distanceHintRef.current = 'no_face';
    setDistanceHint('no_face');
    finalizedRef.current = false;
    capturedVideoRef.current = null;
    setPhase('detecting');

    // Start recording the whole attempt as PAD evidence (best-effort).
    chunksRef.current = [];
    const recorder = makeRecorder(stream);
    recorderRef.current = recorder;
    if (recorder) {
      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };
      try {
        recorder.start(500);
      } catch {
        recorderRef.current = null;
      }
    }

    let cancelled = false;

    const finalize = async () => {
      if (finalizedRef.current) return;
      finalizedRef.current = true;

      const video = videoRef.current;
      const canvas = canvasRef.current;
      let photo: string | null = null;
      let qualityIssues: QualityIssue[] = [];
      if (video && canvas) {
        try {
          photo = grabFrame(video, canvas);
          qualityIssues = assessSelfieQuality(canvas).issues;
        } catch {
          photo = null;
        }
      }

      // The scorer yields ADVISORY telemetry scalars (blink/turn counts, presence
      // score) + the per-user EAR calibration ONLY — it does NOT decide pass/fail.
      // Gating authority is the challenge machine (sequence completion) + the
      // anti-spoof gate above (`stateAfter.done && spoof.isLive`); the SERVER's
      // recomputed verdict is the trustworthy one. So these numbers are telemetry,
      // never a second liveness verdict (see the advisory ClientLivenessSignal).
      const result = scorer.result();
      const videoBase64 = await stopRecorderToBase64(recorderRef.current, chunksRef.current);
      if (cancelled) return;

      capturedVideoRef.current = videoBase64;
      setBase64(photo);
      setIssues(qualityIssues);
      setOutcome(result);
      setPhase('captured');
    };

    const interval = window.setInterval(() => {
      const video = videoRef.current;
      if (cancelled || finalizedRef.current || !video || video.readyState < 2) return;

      const nowMs = performance.now();

      void ml.detectFaceMesh(video, nowMs).then((frame) => {
        if (cancelled || finalizedRef.current) return;

        if (!frame) {
          scorer.ingest({ detected: false, ear: 0, yaw: 0, centeredness: 0 });
          const spoof = antiSpoof.analyzeNoFace();
          setAntiSpoofReason(spoof.reason);
          if (distanceHintRef.current !== 'no_face') {
            distanceHintRef.current = 'no_face';
            setDistanceHint('no_face');
          }
          // Advance the machine with a no-face frame (a bad stability frame).
          setMachineState(
            machine.observe(
              { detected: false, ear: 0, yaw: 0, centeredness: 0 },
              video.videoWidth,
              video.videoHeight,
              nowMs,
            ),
          );
          return;
        }

        const classified = ml.classifyFaceFrame(frame, video.videoWidth, video.videoHeight);
        scorer.ingest(classified.signal);

        // Collect open-eye EAR samples while still positioning — feeds
        // calibrateBaseline() for a per-user blink threshold (demographic EAR
        // variation). Calibrate once, at the transition into the first challenge.
        const stateBefore = machine.state();
        if (stateBefore.phase === 'positioning') {
          scorer.observeBaselineSample(classified.ear);
        }

        if (classified.distanceHint !== distanceHintRef.current) {
          distanceHintRef.current = classified.distanceHint;
          setDistanceHint(classified.distanceHint);
        }

        // Record the numeric per-frame history — the SERVER re-derives its own
        // verdict from this (server-shaped fields; numeric ONLY: Euler degrees,
        // face-box diagonal, timestamps, and eye-open/smile scalars). Blink is
        // sent as eye-OPEN probability (1 - closed-prob blendshape) so the
        // server's verbatim Dart Schmitt thresholds (arm <0.35, fire >0.65) apply.
        if (firstFrameMsRef.current === null) firstFrameMsRef.current = nowMs;
        const relMs = nowMs - firstFrameMsRef.current;
        const s = classified.signal;
        const yawDeg = s.yawDeg ?? 0;
        const pitchDeg = s.pitchDeg ?? 0;
        const rollDeg = s.rollDeg ?? 0;
        const faceSize = s.faceSize ?? 0;
        const reported: ChallengeReportFrame = {
          yaw: yawDeg,
          pitch: pitchDeg,
          roll: rollDeg,
          face_size: faceSize,
          eye_open: Math.max(0, Math.min(1, 1 - (s.blinkScore ?? 0))),
          smile: s.smileScore ?? 0,
          t_ms: Math.round(relMs),
          ...(s.mouthOpen !== undefined ? { mouth_open: s.mouthOpen } : {}),
        };
        if (historyRef.current.length < MAX_REPORTED_HISTORY) {
          historyRef.current.push(reported);
        }
        // The current step's own evidence, uncapped: this buffer is emptied
        // every time a step is accepted, so it cannot grow without bound.
        stepFramesRef.current.push(reported);

        // Anti-spoof runs on every detected frame's numeric motion sample.
        const spoofFrame: AntiSpoofFrame = {
          yawDeg,
          pitchDeg,
          rollDeg,
          faceSize,
          timestampMs: nowMs,
        };
        const spoof = antiSpoof.analyze(spoofFrame);
        setAntiSpoofReason(spoof.reason);

        // Distance gate: nothing advances while the face is out of range.
        if (distanceHintRef.current !== 'ok') {
          setMachineState(stateBefore);
          return;
        }

        // Drive the pure challenge machine with this frame.
        const stateAfter = machine.observe(
          classified.signal,
          video.videoWidth,
          video.videoHeight,
          nowMs,
        );
        setMachineState(stateAfter);

        // Calibrate the per-user EAR threshold once we leave positioning.
        if (
          !calibratedRef.current &&
          stateBefore.phase === 'positioning' &&
          stateAfter.phase !== 'positioning'
        ) {
          scorer.calibrateBaseline();
          calibratedRef.current = true;
        }

        // A challenge just completed. Report it and ask for the next — the
        // server decides whether the session continues, not this component.
        if (stateAfter.done && !stepBusyRef.current) {
          const session = sessionRef.current;
          const outstanding = session?.challenge;

          if (session && outstanding) {
            stepBusyRef.current = true;
            const frames = stepFramesRef.current;
            stepFramesRef.current = [];
            void submitLivenessStep(token, session.sessionId, outstanding.challengeId, frames)
              .then(next => {
                sessionRef.current = next;
                const action = next.challenge?.action as ChallengeType | undefined;

                if (next.challenge?.action === 'flash' && next.challenge.sequence) {
                  // Not a pose: the flash stage takes over until its clip is in.
                  // stepBusy stays held so nothing finalizes underneath it.
                  stepBusyRef.current = true;
                  setFlash({
                    challengeId: next.challenge.challengeId,
                    sequence: next.challenge.sequence,
                    stream: streamRef.current,
                  });
                  return;
                }
                if (action) {
                  machineRef.current?.appendChallenge(action);
                  setChallengeOrder(prev => [...prev, action]);
                } else if (next.state !== 'passed') {
                  // Refused or failed. Surfaced rather than retried: a retry
                  // would let a held recording be resubmitted against a
                  // challenge the applicant has already been told.
                  setError('liveness_challenge_failed');
                }
              })
              .catch(() => setError('liveness_challenge_failed'))
              .finally(() => {
                stepBusyRef.current = false;
              });
          }
        }

        // Finalize only when the SERVER says the session passed AND anti-spoof
        // agrees. `stateAfter.done` alone means only that every challenge
        // issued SO FAR is complete.
        if (
          stateAfter.done &&
          spoof.isLive &&
          !stepBusyRef.current &&
          sessionRef.current?.state === 'passed'
        ) {
          void finalize();
        }
      });
    }, FRAME_INTERVAL_MS);

    return () => {
      cancelled = true;
      window.clearInterval(interval);
      const rec = recorderRef.current;
      if (rec && rec.state !== 'inactive') {
        try {
          rec.stop();
        } catch {
          /* ignore */
        }
      }
    };
  }, [cameraReady, modelReady, retakeNonce]);

  const retake = useCallback(() => {
    // The customer rejected this FINALIZED run → one liveness attempt (client).
    // Reason reflects the quality gate when present, else a plain retake. The
    // dedup key is the current run's retakeNonce so a blocked-submit + retake on
    // ONE run collapse to a single attempt (see reportAttempt dedup).
    const firstIssue = issues[0];
    reportAttempt(token, {
      stepKey: 'awaiting_liveness',
      stepKind: 'liveness',
      reason: firstIssue ? classifyLivenessQuality(firstIssue) : 'retaken',
      origin: 'client',
      attemptKey: `awaiting_liveness:${retakeNonce}`,
    });
    setBase64(null);
    setOutcome(null);
    setIssues([]);
    setError(null);
    capturedVideoRef.current = null;
    // Re-arm the detection effect for a fresh attempt.
    setRetakeNonce((n) => n + 1);
  }, [token, issues, retakeNonce]);

  const submit = useCallback(async () => {
    if (!base64 || !outcome) return;
    // Soft-block on quality heuristics (dark/bright/blank) — force a retake.
    if (issues.length > 0) {
      // Blocked submit of a finalized run → one liveness attempt (client). Same
      // retakeNonce key as retake() so blocked-submit + retake collapse to one.
      const firstIssue = issues[0];
      reportAttempt(token, {
        stepKey: 'awaiting_liveness',
        stepKind: 'liveness',
        reason: firstIssue ? classifyLivenessQuality(firstIssue) : 'retaken',
        origin: 'client',
        attemptKey: `awaiting_liveness:${retakeNonce}`,
      });
      return;
    }
    setPhase('submitting');
    setError(null);
    try {
      const next = await submitCapturedSelfie(token, flowId, base64, {
        videoBase64: capturedVideoRef.current,
        flashVideoBase64: flashClipRef.current,
        clientLiveness: toClientLivenessSignal(outcome),
        // Numeric history + issued challenges ride TOP-LEVEL for the server to
        // re-validate (see ChallengeReport). The browser never sends a verdict.
        challengeReport: {
          challenges: challengeOrder,
          frames: historyRef.current,
        },
      });
      stopCamera();
      onAdvance(next);
    } catch (e) {
      setError(e instanceof CollectionFlowError ? `${e.status}` : 'submit failed');
      setPhase('captured');
    }
  }, [base64, outcome, issues, token, flowId, retakeNonce, onAdvance, stopCamera, challengeOrder]);

  if (phase === 'denied') {
    return (
      <section
        className="liveness liveness--denied"
        aria-label={t('flow.liveness.title', { defaultValue: 'Biometric verification' })}
      >
        <StatusGlyph kind="error" />
        <p role="alert" className="alert">
          {t('flow.liveness.denied', {
            defaultValue: 'Camera access is required to continue.',
          })}
        </p>
        {onCameraUnavailable ? (
          <button
            type="button"
            className="btn"
            onClick={onCameraUnavailable}
            data-track="use_phone_instead"
            data-testid="use-phone-instead"
          >
            {t('flow.camera.usePhoneInstead', { defaultValue: 'Use my phone instead' })}
          </button>
        ) : null}
      </section>
    );
  }

  // ---- Review: circular preview + quality issues + Retake / Submit --------
  if (phase === 'captured' || phase === 'submitting') {
    const blocked = issues.length > 0;
    return (
      <section
        className="liveness liveness--review"
        aria-label={t('flow.liveness.retakeTitle', { defaultValue: 'Retake photo' })}
        data-testid="liveness-review"
      >
        <p className="liveness-review-label">
          {t('flow.liveness.retakeTitle', { defaultValue: 'Retake photo' })}
        </p>

        {base64 ? (
          <div className="liveness-circle">
            <img
              src={`data:image/jpeg;base64,${base64}`}
              alt={t('flow.liveness.previewAlt', { defaultValue: 'Captured selfie preview' })}
              className="liveness-circle-img"
            />
          </div>
        ) : null}

        {issues.length > 0 ? (
          <ul className="liveness-issues" role="alert">
            {issues.map((issue) => (
              <li key={issue}>
                <span className="liveness-issue-icon" aria-hidden="true">
                  !
                </span>
                {t(`flow.liveness.quality.${issue}`, {
                  defaultValue: qualityDefault(issue),
                })}
              </li>
            ))}
          </ul>
        ) : (
          <p className="liveness-ok">
            {t('flow.liveness.confirmed', {
              defaultValue: 'Liveness confirmed — submit to continue.',
            })}
          </p>
        )}

        {error ? (
          <p role="alert" className="alert">
            {error}
          </p>
        ) : null}

        <div className="liveness-actions">
          {phase === 'submitting' ? (
            <p className="step-text">
              {t('flow.liveness.submitting', { defaultValue: 'Submitting…' })}
            </p>
          ) : (
            <>
              <button
                type="button"
                className="btn liveness-btn-outline"
                onClick={retake}
                data-testid="liveness-retake"
                data-track="retake_selfie"
              >
                {t('flow.liveness.retake', { defaultValue: 'Retake' })}
              </button>
              {!blocked ? (
                <button
                  type="button"
                  className="btn"
                  onClick={() => void submit()}
                  data-testid="liveness-submit"
                  data-track="submit_selfie"
                >
                  {t('flow.liveness.submit', { defaultValue: 'Submit' })}
                </button>
              ) : null}
            </>
          )}
        </div>
        <canvas ref={canvasRef} hidden data-testid="liveness-canvas" />
      </section>
    );
  }

  // ---- Live oval stage: model/camera loading + active challenge -----------
  const isPreparing = !cameraReady || !modelReady;
  const stageIndex = machineState?.index ?? 0;
  const promptText = isPreparing
    ? t('flow.liveness.loadingModels', { defaultValue: 'Loading face models…' })
    : distanceHint !== 'ok'
      ? t(`flow.liveness.distance.${distanceHint}`, {
          defaultValue: distanceDefault(distanceHint),
        })
      : machinePromptKey(machineState, antiSpoofReason)
        ? t(`flow.liveness.${machinePromptKey(machineState, antiSpoofReason)}`, {
            defaultValue: machinePromptDefault(machineState, antiSpoofReason),
          })
        : t('flow.liveness.prompts.center', {
            defaultValue: 'Center your face in the oval',
          });

  const onFlashDone = (clip: null | string) => {
    flashClipRef.current = clip;
    const session = sessionRef.current;
    const current = flash;
    setFlash(null);
    if (!session || !current) {
      stepBusyRef.current = false;
      return;
    }
    // The browser cannot show the flash was met — the sidecar judges the clip.
    void submitLivenessStep(token, session.sessionId, current.challengeId, [])
      .then((next) => {
        sessionRef.current = next;
        if (next.state !== 'passed') setError('liveness_challenge_failed');
      })
      .catch(() => setError('liveness_challenge_failed'))
      .finally(() => {
        stepBusyRef.current = false;
      });
  };

  if (flash) {
    return <FlashStage onDone={onFlashDone} sequence={flash.sequence} stream={flash.stream} />;
  }

  return (
    <section
      className="liveness liveness--live"
      aria-label={t('flow.liveness.title', { defaultValue: 'Biometric verification' })}
      data-testid="liveness-live"
    >
      <header className="liveness-brand">
        <img
          src="/logo.png"
          alt=""
          width={22}
          height={22}
          className="liveness-brand-mark"
          aria-hidden="true"
          decoding="async"
        />
        <span>trustsy</span>
      </header>

      <div className="liveness-stage">
        <video
          ref={videoRef}
          className="liveness-video"
          playsInline
          muted
          data-testid="liveness-video"
        />
        {/* Oval cutout mask: purple vignette with transparent face hole */}
        <div className="liveness-mask" aria-hidden="true" />
        <div className="liveness-oval-ring" aria-hidden="true" />

        <div className="liveness-progress" role="list" aria-label="Liveness steps">
          {challengeOrder.map((key, i) => (
            <span
              key={`${key}-${i}`}
              role="listitem"
              className={
                i < stageIndex
                  ? 'liveness-dot liveness-dot--done'
                  : i === stageIndex
                    ? 'liveness-dot liveness-dot--active'
                    : 'liveness-dot'
              }
            />
          ))}
        </div>

        <p className="liveness-prompt" data-testid="liveness-prompt">
          {promptText}
        </p>
      </div>

      <canvas ref={canvasRef} hidden data-testid="liveness-canvas" />

      {error ? (
        <p role="alert" className="alert" data-testid="liveness-error">
          {t(livenessErrorKey(error))}
        </p>
      ) : null}
    </section>
  );
}

// i18n key suffix for the live prompt, derived from the machine phase (+ the
// anti-spoof reason while positioning). Returns the dotted suffix under
// `flow.liveness.*` (e.g. 'prompts.smile', 'antispoof.move_naturally').
function machinePromptKey(
  state: MachineState | null,
  antiSpoofReason: string | null,
): string {
  if (!state || state.phase === 'positioning') {
    // Surface the anti-spoof coaching while the user is getting positioned.
    if (antiSpoofReason && antiSpoofReason !== 'live' && antiSpoofReason !== 'no_face') {
      return `antispoof.${antiSpoofReason}`;
    }

    return 'prompts.center';
  }

  if (state.phase === 'neutral') {
    return 'prompts.neutral';
  }

  if (state.phase === 'challenge' && state.currentChallenge) {
    return `prompts.${state.currentChallenge}`;
  }

  return 'prompts.center';
}

function machinePromptDefault(
  state: MachineState | null,
  antiSpoofReason: string | null,
): string {
  if (!state || state.phase === 'positioning') {
    if (antiSpoofReason && antiSpoofReason !== 'live' && antiSpoofReason !== 'no_face') {
      return antiSpoofDefault(antiSpoofReason);
    }

    return 'Center your face in the oval';
  }

  if (state.phase === 'neutral') {
    return 'Hold still and look straight ahead';
  }

  if (state.phase === 'challenge' && state.currentChallenge) {
    return challengeDefault(state.currentChallenge);
  }

  return 'Center your face in the oval';
}

function challengeDefault(challenge: ChallengeType): string {
  switch (challenge) {
    case 'smile':
      return 'Smile';
    case 'blink':
      return 'Blink';
    case 'turn_left':
      return 'Slowly turn your head to the left';
    case 'turn_right':
      return 'Slowly turn your head to the right';
    case 'nod':
      return 'Nod your head';
    case 'head_shake':
      return 'Shake your head';
    case 'open_mouth':
      return 'Open your mouth';
    case 'move_closer':
      return 'Move a little closer to the camera';
    case 'move_back':
      return 'Move a little further from the camera';
  }
}

function antiSpoofDefault(reason: string): string {
  switch (reason) {
    case 'collecting':
      return 'Hold steady…';
    case 'move_naturally':
      return 'Move your head naturally';
    case 'move_depth':
      return 'Move a little closer or farther';
    case 'verifying':
      return 'Verifying…';
    default:
      return 'Center your face in the oval';
  }
}

function distanceDefault(hint: DistanceHint): string {
  switch (hint) {
    case 'too_close':
      return 'Move farther from the camera';
    case 'too_far':
      return 'Move closer to the camera';
    case 'no_face':
    default:
      return 'Position your face inside the oval';
  }
}

function qualityDefault(issue: QualityIssue): string {
  switch (issue) {
    case 'tooDark':
      return 'Selfie is too dark — try better lighting';
    case 'tooBright':
      return 'Selfie is too bright — reduce glare';
    case 'lowDetail':
      return 'Face not clear enough — move closer and hold steady';
  }
}
